import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
} from "reactstrap";

import dayjs from "dayjs";
import { toast } from "react-toastify";

import { getCommonAttachmentById, getRoadTestScheduleForCallDetails, resolveCommonAttachmentDownloadUrl, submitRoadTestScheduleForCallDetails } from "model/CallModel";

import {
    getCandidateTestEditNode,
    resolveCandidateApplicationStatusTestTitle,
    resolveCandidateTestMultipartIdentifiers,
} from "helpers/callDetailsCandidateApplicationStatusTestPresentation";

import { downloadFileFromUrl } from "helpers/GeneralHelper";

const SELECT_STYLES = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    menu: base => ({ ...base, zIndex: 9999 }),
};

const SCHEDULE_ACTION_OPTIONS = [
    { value: "schedule", label: "Schedule" },
    { value: "completed", label: "Completed" },
];

const PENDING_ACTION_OPTIONS = [
    { value: "cancelled", label: "Cancelled" },
    { value: "completed", label: "Completed" },
];

const COMPLETED_CANCEL_ONLY_OPTIONS = [{ value: "cancelled", label: "Cancelled" }];

const FILE_ACCEPT =
    ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,image/gif";

const text = value => String(value || "").trim();

const lower = value => text(value).toLowerCase();

const parseRawData = row => {
    try {
        return JSON.parse(getCandidateTestEditNode(row)?.rawData || "{}");
    } catch {
        return {};
    }
};

const getDateTime = row => {
    const edit = getCandidateTestEditNode(row);
    const raw = parseRawData(row);

    const date =
        raw.scheduleDate ||
        raw.testDate ||
        edit?.scheduleDate ||
        row?.testDate;

    const time =
        raw.scheduleTime ||
        raw.testTime ||
        edit?.scheduleTime;

    return {
        date: date && dayjs(date).isValid()
            ? dayjs(date).format("YYYY-MM-DD")
            : "",

        time: time
            ? /^\d{2}:\d{2}/.test(time)
                ? time.slice(0, 5)
                : dayjs(time).isValid()
                  ? dayjs(time).format("HH:mm")
                  : ""
            : "",
    };
};

const getInitialAction = row => {
    const combined = lower(`
        ${row?.statusLabel}
        ${row?.rawStatus}
        ${row?.displayRawStatus}
        ${getCandidateTestEditNode(row)?.status}
    `);

    return SCHEDULE_ACTION_OPTIONS.find(option =>
        combined.includes(option.value)
    ) || null;
};

const getInitialAddress = row => {
    const raw = parseRawData(row);

    return text(
        raw.roadTestAddress ||
        raw.address ||
        raw.testAddress ||
        row?.roadTestAddress ||
        row?.testAddress ||
        row?.address
    );
};

const getInitialMessage = row => {
    const raw = parseRawData(row);

    return text(
        raw.messageToDriver ||
        raw.driverMessage ||
        raw.specialMessage ||
        row?.messageToDriver ||
        row?.driverMessage ||
        row?.specialMessage
    );
};

const getAttachmentId = row =>
    text(
        getCandidateTestEditNode(row)?.attachmentId ||
        row?.attachmentId
    );

const parseRoadTestApiDateTime = (testDate, testDateTimeMinutes) => {
    const minutesRaw = Number(testDateTimeMinutes);
    const minutes = Number.isFinite(minutesRaw) ? Math.max(0, Math.floor(minutesRaw)) : 0;

    if (testDate != null && String(testDate).trim()) {
        const rawStr = String(testDate);
        const d = dayjs(testDate);
        if (d.isValid()) {
            const hasTime =
                rawStr.includes("T") ||
                rawStr.includes(":") ||
                / \d{1,2}:\d{2}/.test(rawStr);
            const dateStr = d.format("YYYY-MM-DD");
            const timeStr = hasTime
                ? d.format("HH:mm")
                : minutes > 0
                    ? `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`
                    : "";
            return { date: dateStr, time: timeStr };
        }
    }

    if (minutes > 0) {
        return {
            date: "",
            time: `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`,
        };
    }

    return { date: "", time: "" };
};

const isRoadTestPendingRawStatus = rawStatus =>
    lower(rawStatus) === "pending";

const isRoadTestCompletedRawStatus = rawStatus => {
    const s = lower(rawStatus);
    return s === "completed" || s === "complete" || s === "yes";
};

const getActionFromRoadTestRawStatus = (rawStatus, actionOpts = []) => {
    const combined = lower(rawStatus);
    if (!combined) return null;
    if (
        isRoadTestPendingRawStatus(rawStatus) ||
        isRoadTestCompletedRawStatus(rawStatus)
    ) {
        return null;
    }

    const directMatch = actionOpts.find(o => lower(o.value) === combined);
    if (directMatch) return directMatch;
    if (combined === "completed" || combined.includes("complete")) {
        return actionOpts.find(o => o.value === "completed") ?? null;
    }
    if (combined === "cancel" || combined === "cancelled" || combined.includes("cancel")) {
        return (
            actionOpts.find(o => o.value === "cancelled" || o.value === "cancel") ??
            null
        );
    }
    if (combined === "schedule" || combined.includes("schedule")) {
        return actionOpts.find(o => o.value === "schedule") ?? null;
    }

    // Legacy API values (yes / no)
    if (combined === "yes" || combined.includes("pass")) {
        return actionOpts.find(o => o.value === "completed") ?? null;
    }
    if (combined === "no") {
        return actionOpts.find(o => o.value === "schedule") ?? null;
    }

    return null;
};

const buildRoadTestSubmitIsoDate = (dateStr, timeStr) => {
    const d = text(dateStr);
    if (!d) return null;
    const t = text(timeStr);
    const parts = t ? t.split(":") : ["0", "0"];
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    const h = Number.isFinite(hh) ? Math.min(23, Math.max(0, hh)) : 0;
    const m = Number.isFinite(mm) ? Math.min(59, Math.max(0, mm)) : 0;
    const timeNorm = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
    const parsed = dayjs(`${d}T${timeNorm}`);
    return parsed.isValid() ? parsed.toISOString() : null;
};

const computeRoadTestSubmitMinutes = timeStr => {
    const t = text(timeStr);
    if (!t) return 0;
    const parts = t.split(":");
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
    return Math.min(23 * 60 + 59, Math.max(0, h * 60 + m));
};

const buildTimeSlotOptions = (timeSlots = []) => {
    if (!Array.isArray(timeSlots)) return [];
    return timeSlots
        .map(slot => {
            const minutes = Number(slot?.minutesFromMidnight);
            if (!Number.isFinite(minutes)) return null;
            const label =
                text(slot?.label) ||
                `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
                    minutes % 60
                ).padStart(2, "0")}`;
            return { value: String(minutes), label };
        })
        .filter(Boolean);
};

const resolveTimeLabelFromMinutes = (minutesRaw, timeSlots = []) => {
    const minutes = Number(minutesRaw);
    if (!Number.isFinite(minutes) || minutes < 0) return "";
    const options = buildTimeSlotOptions(timeSlots);
    const match = options.find(o => Number(o.value) === minutes);
    if (match) return match.label;
    if (minutes === 0) return "";
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
        minutes % 60
    ).padStart(2, "0")}`;
};

const resolveMinutesFromTimeSelection = (timeLabel, timeSlots = [], slotOptions = []) => {
    const t = text(timeLabel);
    if (!t) return 0;
    const options =
        Array.isArray(slotOptions) && slotOptions.length
            ? slotOptions
            : buildTimeSlotOptions(timeSlots);
    const match = options.find(o => o.label === t);
    if (match) {
        const minutes = Number(match.value);
        return Number.isFinite(minutes) ? minutes : 0;
    }
    return computeRoadTestSubmitMinutes(t);
};

const normalizeRoadTestActionOption = item => {
    const value = lower(item?.value);
    if (!value) return null;
    if (value === "cancel" || value === "cancelled") {
        return { value: "cancelled", label: text(item?.label) || "Cancelled" };
    }
    return { value, label: text(item?.label) || value };
};

const buildActionOptions = (
    availableActions,
    isPendingMode = false,
    isCompletedMode = false
) => {
    if (isCompletedMode) {
        if (Array.isArray(availableActions) && availableActions.length) {
            const cancelOnly = availableActions
                .map(normalizeRoadTestActionOption)
                .filter(o => o?.value === "cancelled" || o?.value === "cancel");
            if (cancelOnly.length) return cancelOnly;
        }
        return COMPLETED_CANCEL_ONLY_OPTIONS;
    }

    const fallback = isPendingMode ? PENDING_ACTION_OPTIONS : SCHEDULE_ACTION_OPTIONS;
    if (!Array.isArray(availableActions) || !availableActions.length) {
        return fallback;
    }
    const fromApi = availableActions
        .map(normalizeRoadTestActionOption)
        .filter(Boolean);
    if (!fromApi.length) {
        return fallback;
    }
    if (isPendingMode) {
        const pendingValues = new Set(["cancel", "cancelled", "completed"]);
        const pendingOnly = fromApi.filter(o => pendingValues.has(o.value));
        return pendingOnly.length ? pendingOnly : fallback;
    }
    return fromApi;
};

const resolveRawStatusForRoadTestSubmit = (actionValue) => {
    const action = lower(actionValue);
    if (action === "completed") return "completed";
    if (action === "schedule") return "schedule";
    if (action === "cancel" || action === "cancelled") return "cancelled";
    return action || "schedule";
};

const resolveRoadTestStatusBanner = (snapshot = {}) => {
    const banner = text(snapshot?.statusBanner);
    if (banner) return banner;
    const display = text(snapshot?.testDateDisplay);
    if (display) {
        return `Road Test already Scheduled. (Date : ${display} )`;
    }
    const dt = parseRoadTestApiDateTime(
        snapshot?.testDate,
        snapshot?.testDateTimeMinutes
    );
    if (dt.date && dt.time) {
        const formattedDate = dayjs(dt.date).isValid()
            ? dayjs(dt.date).format("MM-DD-YYYY")
            : dt.date;
        return `Road Test already Scheduled. (Date : ${dt.time} ${formattedDate} )`;
    }
    return "";
};

function CallDetailsRoadTestScheduleUpdateModal({
    isOpen,
    onRequestClose,
    callDetailsRouteParameterId: _callDetailsRouteParameterId,
    candidateTestActionBodyCandidateClientId,
    candidateTestActionBodyCandidateId,
    applicationStatusTestRow,
    actionDefinition,
    onSaveSuccess,
}) {
    const [action, setAction] = useState(null);
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [address, setAddress] = useState("");
    const [message, setMessage] = useState("");
    const [attachmentId, setAttachmentId] = useState("");
    const [file, setFile] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [roadTestLoading, setRoadTestLoading] = useState(false);
    const [roadTestApiSnapshot, setRoadTestApiSnapshot] = useState(null);
    const [timeSlotOptions, setTimeSlotOptions] = useState([]);
    const [actionOptions, setActionOptions] = useState(SCHEDULE_ACTION_OPTIONS);

    const isPendingMode = isRoadTestPendingRawStatus(roadTestApiSnapshot?.rawStatus);
    const isCompletedMode = isRoadTestCompletedRawStatus(roadTestApiSnapshot?.rawStatus);
    const showScheduleFields =
        !isPendingMode &&
        !isCompletedMode &&
        roadTestApiSnapshot?.showScheduleFields !== false;
    const showMessageToDriver = isPendingMode || isCompletedMode || showScheduleFields;
    const statusBannerText = isPendingMode
        ? resolveRoadTestStatusBanner(roadTestApiSnapshot)
        : "";
    const allowFileUpload =
        roadTestApiSnapshot?.allowFileUpload !== false;

    const isCompleted = action?.value === "completed";
    const isCancelled = action?.value === "cancelled" || action?.value === "cancel";
    const notesRequired = Boolean(roadTestApiSnapshot?.notesRequired);
    const showFileUpload =
        !isCompletedMode && isCompleted && allowFileUpload;

    useEffect(() => {
        if (!isOpen || !applicationStatusTestRow) return;

        const values = getDateTime(applicationStatusTestRow);

        setDate(values.date);
        setTime(values.time);
        setAddress(getInitialAddress(applicationStatusTestRow));
        setMessage(getInitialMessage(applicationStatusTestRow));
        setAttachmentId(getAttachmentId(applicationStatusTestRow));
        setAction(getInitialAction(applicationStatusTestRow));
        setFile(null);
        setRoadTestApiSnapshot(null);
        setTimeSlotOptions([]);
        setActionOptions(SCHEDULE_ACTION_OPTIONS);

        const candidateClientId = text(candidateTestActionBodyCandidateClientId);
        if (!candidateClientId) {
            setRoadTestLoading(false);
            return;
        }

        let cancelled = false;
        setRoadTestLoading(true);

        (async () => {
            try {
                const response = await getRoadTestScheduleForCallDetails({
                    candidateClientId,
                });
                const payload = response?.data?.data ?? response?.data ?? null;

                if (cancelled || !payload || typeof payload !== "object") {
                    return;
                }

                setRoadTestApiSnapshot(payload);

                const slots = buildTimeSlotOptions(payload.timeSlots);
                setTimeSlotOptions(slots);

                const pendingMode = isRoadTestPendingRawStatus(payload.rawStatus);
                const completedMode = isRoadTestCompletedRawStatus(payload.rawStatus);
                const actionOpts = buildActionOptions(
                    payload.availableActions,
                    pendingMode,
                    completedMode
                );
                setActionOptions(actionOpts);

                const dt = parseRoadTestApiDateTime(
                    payload.testDate,
                    payload.testDateTimeMinutes
                );
                setDate(dt.date);
                setTime(
                    resolveTimeLabelFromMinutes(
                        payload.testDateTimeMinutes,
                        payload.timeSlots
                    ) || dt.time
                );

                setAddress(
                    text(payload.location) ||
                        text(payload.defaultLocation) ||
                        getInitialAddress(applicationStatusTestRow)
                );
                setMessage(
                    text(payload.notes) ||
                        text(payload.defaultNotes) ||
                        getInitialMessage(applicationStatusTestRow)
                );

                const att = text(payload.attachmentId);
                if (att) {
                    setAttachmentId(att);
                }

                if (!pendingMode && !completedMode) {
                    const apiAction = getActionFromRoadTestRawStatus(
                        payload.rawStatus,
                        actionOpts
                    );
                    if (apiAction) {
                        setAction(
                            actionOpts.find(o => o.value === apiAction.value) ||
                                apiAction
                        );
                    }
                } else {
                    setAction(null);
                }
            } finally {
                if (!cancelled) {
                    setRoadTestLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, applicationStatusTestRow, candidateTestActionBodyCandidateClientId]);

    useEffect(() => {
        if (!isCompleted) {
            setFile(null);
        }
    }, [isCompleted]);

    const downloadAttachment = async () => {
        if (!attachmentId) return;

        setDownloading(true);

        try {
            const response = await getCommonAttachmentById({ attachmentId });
            if (response?.data) {
                const downloadUrl = resolveCommonAttachmentDownloadUrl(response);
                if (downloadUrl) {
                    await downloadFileFromUrl(downloadUrl);
                } else {
                    toast.error(
                        response?.message || "Could not resolve download URL for this attachment."
                    );
                }
            } else {
                toast.error(response?.message || "Could not load attachment.");
            }
        } finally {
            setDownloading(false);
        }
    };

    const submit = async event => {
        event.preventDefault();

        if (!action?.value) {
            toast.warning("Please select an action.");
            return;
        }

        if (roadTestLoading) {
            return;
        }

        if (notesRequired && !text(message)) {
            toast.warning("Message to driver is required.");
            return;
        }

        const {
            candidateClientId,
            jobRequireId,
        } = resolveCandidateTestMultipartIdentifiers({
            applicationStatusTestRow,
            candidateTestActionBodyCandidateClientId,
            candidateTestActionBodyCandidateId,
        });

        const api = roadTestApiSnapshot && typeof roadTestApiSnapshot === "object"
            ? roadTestApiSnapshot
            : null;

        const resolvedCandidateClientId =
            text(api?.candidateClientId) || text(candidateClientId);
        const resolvedJobRequireId =
            text(api?.jobRequireId) || text(jobRequireId);
        const resolvedCandidateTestId =
            text(api?.candidateTestId) || text(applicationStatusTestRow?.candidateTestId);

        if (!resolvedCandidateClientId) {
            toast.error("Candidate client id is missing.");
            return;
        }

        if (!resolvedJobRequireId) {
            toast.error("Job requirement id is missing.");
            return;
        }

        const testDateIso = showScheduleFields
            ? buildRoadTestSubmitIsoDate(date, time)
            : api?.testDate == null || api?.testDate === ""
                ? null
                : String(api.testDate);
        const testDateTimeMinutes = showScheduleFields
            ? resolveMinutesFromTimeSelection(time, api?.timeSlots, timeSlotOptions)
            : Number(api?.testDateTimeMinutes) || 0;
        const resolvedLocation = showScheduleFields
            ? text(address)
            : text(api?.location) || text(api?.defaultLocation);

        const payload = {
            candidateClientId: resolvedCandidateClientId,
            jobRequireId: resolvedJobRequireId,
            candidateTestId: resolvedCandidateTestId,
            rawStatus: resolveRawStatusForRoadTestSubmit(action?.value),
            testDate: testDateIso,
            testDateTimeMinutes,
            location: resolvedLocation,
            notes: text(message),
            attachmentId: file ? "" : text(attachmentId),
            file: showFileUpload && file instanceof File ? file : null,
        };

        setSaving(true);

        try {
            const response = await submitRoadTestScheduleForCallDetails(payload);

            if (
                response?.success ||
                response?.isSuccess ||
                response?.status === true ||
                response?.status === "success"
            ) {
                toast.success(
                    response?.message || "Saved successfully."
                );

                await onSaveSuccess?.();

                onRequestClose?.();
            }
        } finally {
            setSaving(false);
        }
    };

    if (!applicationStatusTestRow || !actionDefinition) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={onRequestClose}
            centered
            size="md"
            className="modal-full-height"
        >
            <ModalHeader toggle={onRequestClose}>
                {`UPDATE - ${resolveCandidateApplicationStatusTestTitle(
                    applicationStatusTestRow
                ).toUpperCase()}`}
            </ModalHeader>

            <ModalBody>
                <Form onSubmit={submit}>
                    {statusBannerText ? (
                        <p className="text-muted mb-3 mb-md-4">{statusBannerText}</p>
                    ) : null}

                    <FormGroup>
                        <Label className="fw-semibold">
                            Action
                        </Label>

                        <Select
                            options={actionOptions}
                            value={action}
                            onChange={setAction}
                            isClearable={!isPendingMode && !isCompletedMode}
                            isLoading={roadTestLoading}
                            placeholder="Choose..."
                            className="select2-selection"
                            menuPortalTarget={document.body}
                            styles={SELECT_STYLES}
                        />
                    </FormGroup>

                    {showScheduleFields ? (
                        <>
                            <FormGroup>
                                <Label className="fw-semibold">
                                    Date
                                </Label>

                                <Input
                                    type="date"
                                    value={date}
                                    onChange={e => setDate(e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label className="fw-semibold">
                                    Time
                                </Label>

                                {timeSlotOptions.length > 0 ? (
                                    <Select
                                        options={timeSlotOptions}
                                        value={
                                            timeSlotOptions.find(o => o.label === time) ||
                                            null
                                        }
                                        onChange={opt => setTime(opt?.label || "")}
                                        isClearable
                                        isLoading={roadTestLoading}
                                        placeholder="Select time"
                                        className="select2-selection"
                                        menuPortalTarget={document.body}
                                        styles={SELECT_STYLES}
                                    />
                                ) : (
                                    <Input
                                        type="time"
                                        value={time}
                                        onChange={e => setTime(e.target.value)}
                                        disabled={roadTestLoading}
                                    />
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label className="fw-semibold">
                                    Address
                                </Label>

                                <Input
                                    type="textarea"
                                    rows={4}
                                    value={address}
                                    onChange={e => setAddress(e.target.value)}
                                />
                            </FormGroup>
                        </>
                    ) : null}

                    {showMessageToDriver ? (
                        <FormGroup>
                            <Label className="fw-semibold">
                                Message to driver
                            </Label>

                            <Input
                                type="textarea"
                                rows={4}
                                value={message}
                                required={notesRequired && !isCancelled}
                                onChange={e => setMessage(e.target.value)}
                            />
                        </FormGroup>
                    ) : null}

                    {showFileUpload ? (
                        <FormGroup>
                            <Label className="fw-semibold">
                                File upload (doc,pdf,docx,image)
                            </Label>

                            <Input
                                type="file"
                                accept={FILE_ACCEPT}
                                onChange={e =>
                                    setFile(e.target.files?.[0] || null)
                                }
                            />

                            {attachmentId && !file ? (
                                <div className="d-flex align-items-center gap-2 mt-2">
                                    <small className="text-muted">
                                        Existing attachment will be kept.
                                    </small>

                                    <Button
                                        size="sm"
                                        color="secondary"
                                        outline
                                        type="button"
                                        disabled={downloading}
                                        onClick={downloadAttachment}
                                    >
                                        {downloading ? "Downloading..." : "Download"}
                                    </Button>
                                </div>
                            ) : null}
                        </FormGroup>
                    ) : null}

                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            color="primary"
                            type="submit"
                            disabled={saving || roadTestLoading}
                        >
                            {roadTestLoading
                                ? "Loading..."
                                : saving
                                    ? "Saving..."
                                    : "Save"}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
}

export default CallDetailsRoadTestScheduleUpdateModal;